/**
 * Priority ("fast") tier map (single source of truth).
 *
 * Each base model maps to the public identifier of the endpoint that serves
 * its faster tier (its own low-parallelism deployment and pricing). Callers
 * reach the tier by calling that id directly, or by sending the base id with
 * `service_tier: "priority" | "fast"`.
 *
 * Two layers read this map:
 *
 *   1. `llm-ops-proxy` rewrites a `service_tier` request's `model` to the tier
 *      endpoint before resolution, so routing, billing, rate limits and the
 *      persisted `request_model` all see the tier endpoint, and stamps
 *      `service_tier: "priority"` on its responses.
 *   2. `model-service`'s OpenRouter model list emits the tier endpoint under its
 *      own id with the tier's context limit.
 */
export const PRIORITY_TIER_MODELS: Readonly<Record<string, string>> = {
  "moonshotai/kimi-k3": "moonshotai/kimi-k3-fast",
  // OpenRouter sends the bare id; the z-ai form is the public slug.
  "glm-5.3": "z-ai/glm-5.3-fast",
  "z-ai/glm-5.3": "z-ai/glm-5.3-fast",
};

/**
 * Context (and max output) advertised for a priority tier. Smaller than the
 * base model's: a ~1M-token prompt takes 50-120 s to prefill — past
 * OpenRouter's ~120 s stream cap — and stalls decode on the tier's
 * low-parallelism instances, which is the speed the tier sells.
 */
export const PRIORITY_TIER_MAX_CONTEXT_TOKENS = 250_000;

/**
 * Priority tiers exempt from `PRIORITY_TIER_MAX_CONTEXT_TOKENS`, listed at
 * their catalog context and max output. Each routes >240K-token prompts to a
 * dedicated 1M-context sibling deployment, so the 1M prefills that would
 * exceed OpenRouter's ~120 s stream cap never land on the fast-tier instances.
 */
export const FULL_CONTEXT_PRIORITY_TIER_MODELS: ReadonlySet<string> = new Set([
  "moonshotai/kimi-k3-fast",
]);

/**
 * Priority tiers listed at a context (and max output) other than
 * `PRIORITY_TIER_MAX_CONTEXT_TOKENS`, sized to what their deployments serve.
 * glm-5.3-fast runs on short-context (64K) pools only.
 */
const PRIORITY_TIER_CONTEXT_TOKENS: Readonly<Record<string, number>> = {
  "z-ai/glm-5.3-fast": 65_536,
};

/**
 * Context and max-output cap to advertise for a priority-tier model id, or
 * null when the tier is listed at its full catalog context.
 */
export function priorityTierMaxContextTokens(tierModel: string): number | null {
  if (FULL_CONTEXT_PRIORITY_TIER_MODELS.has(tierModel)) {
    return null;
  }
  return (
    PRIORITY_TIER_CONTEXT_TOKENS[tierModel] ?? PRIORITY_TIER_MAX_CONTEXT_TOKENS
  );
}

/** The tier a response reports: "priority" for tier-served requests, else null (no stamp). */
export type ServedServiceTier = "priority" | null;

/** Request `service_tier` values that ask for the priority tier. */
const PRIORITY_SERVICE_TIERS = new Set(["priority", "fast"]);

const PRIORITY_TIER_BASE_MODELS = new Map(
  Object.entries(PRIORITY_TIER_MODELS).map(([base, tier]) => [tier, base]),
);

/**
 * Returns the priority-tier model id when the request asks for the priority
 * tier of a model that has one, or null.
 */
export function resolvePriorityTierModel(input: {
  model: string | undefined;
  serviceTier: string | undefined;
}): string | null {
  if (
    input.model === undefined ||
    input.serviceTier === undefined ||
    !PRIORITY_SERVICE_TIERS.has(input.serviceTier)
  ) {
    return null;
  }
  return PRIORITY_TIER_MODELS[input.model] ?? null;
}

/**
 * The tier to report in the response `service_tier` field for an (already
 * tier-rewritten) request: "priority" when it asked for a tier and is served
 * by a priority-tier endpoint, else null so the response is left as the
 * upstream wrote it. OpenRouter's contract reports `priority`, never its
 * `fast` alias.
 */
export function servedServiceTier(input: {
  model: string | undefined;
  serviceTier: string | undefined;
}): ServedServiceTier {
  return input.serviceTier !== undefined &&
    PRIORITY_SERVICE_TIERS.has(input.serviceTier) &&
    input.model !== undefined &&
    PRIORITY_TIER_BASE_MODELS.has(input.model)
    ? "priority"
    : null;
}

/** The base model a priority-tier model id serves, or null. */
export function priorityTierBaseModel(model: string): string | null {
  return PRIORITY_TIER_BASE_MODELS.get(model) ?? null;
}
